import { supabase, supabaseAdmin } from "../lib/supabaseClient";

export type OptionSelectionType = "single" | "multi";

type DatabaseOptionGroupRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  selection_type: OptionSelectionType;
  is_required: boolean;
  min_selections: number;
  max_selections: number | null;
  applies_to_order_types: string[] | null;
  priority: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

type DatabaseOptionChoiceRow = {
  id: string;
  option_group_id: string;
  name: string;
  slug: string | null;
  description: string | null;
  is_default: boolean;
  max_quantity: number | null;
  price_delta: number;
  sort_order: number;
  is_active: boolean;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  option_choice_prices?: DatabaseOptionChoicePriceRow[] | null;
  bundle_components?: DatabaseBundleComponentRow[] | null;
};

type DatabaseOptionChoicePriceRow = {
  id: string;
  option_choice_id: string;
  size_template_id: string | null;
  product_size_id: string | null;
  size_code: string | null;
  price_delta: number;
  price_override: number | null;
  is_available: boolean;
  created_at: string;
  updated_at: string;
};

type DatabaseOptionGroupTargetRow = {
  id: string;
  option_group_id: string;
  item_group_id: string | null;
  menu_category_id: string | null;
  product_id: string | null;
  size_template_id: string | null;
  product_size_id: string | null;
  applies_to_order_types: string[] | null;
  priority: number;
  is_required_override: boolean | null;
  created_at: string;
  option_groups?: DatabaseOptionGroupRow | null;
};

type BundleComponentType = "product" | "category" | "group_slot";

type DatabaseBundleComponentRow = {
  id: string;
  option_choice_id: string;
  component_type: BundleComponentType;
  product_id: string | null;
  menu_category_id: string | null;
  item_group_id: string | null;
  slot_code: string | null;
  quantity: number;
  is_required: boolean;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

export interface OptionGroupRecord {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  selectionType: OptionSelectionType;
  isRequired: boolean;
  minSelections: number;
  maxSelections: number | null;
  appliesToOrderTypes: string[];
  priority: number;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface OptionChoicePriceRecord {
  id: string;
  optionChoiceId: string;
  sizeTemplateId: string | null;
  productSizeId: string | null;
  sizeCode: string | null;
  priceDelta: number;
  priceOverride: number | null;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BundleComponentRecord {
  id: string;
  optionChoiceId: string;
  componentType: BundleComponentType;
  productId: string | null;
  menuCategoryId: string | null;
  itemGroupId: string | null;
  slotCode: string | null;
  quantity: number;
  isRequired: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface OptionChoiceRecord {
  id: string;
  optionGroupId: string;
  name: string;
  slug: string | null;
  description: string | null;
  isDefault: boolean;
  maxQuantity: number | null;
  priceDelta: number;
  sortOrder: number;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  prices: OptionChoicePriceRecord[];
  bundleComponents: BundleComponentRecord[];
}

export interface OptionGroupTargetRecord {
  id: string;
  optionGroupId: string;
  itemGroupId: string | null;
  menuCategoryId: string | null;
  productId: string | null;
  sizeTemplateId: string | null;
  productSizeId: string | null;
  appliesToOrderTypes: string[];
  priority: number;
  isRequiredOverride: boolean | null;
  createdAt: string;
  optionGroup?: OptionGroupRecord | null;
}

export interface OptionGroupDetail extends OptionGroupRecord {
  choices: OptionChoiceRecord[];
  targets: OptionGroupTargetRecord[];
}

const adminClient = supabaseAdmin ?? supabase;

const mapOptionGroup = (row: DatabaseOptionGroupRow): OptionGroupRecord => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  description: row.description,
  selectionType: row.selection_type,
  isRequired: Boolean(row.is_required),
  minSelections: row.min_selections,
  maxSelections: row.max_selections,
  appliesToOrderTypes: row.applies_to_order_types ?? [
    "delivery",
    "takeaway",
    "dine_in",
  ],
  priority: row.priority,
  metadata: row.metadata ?? null,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapChoicePrice = (
  row: DatabaseOptionChoicePriceRow,
): OptionChoicePriceRecord => ({
  id: row.id,
  optionChoiceId: row.option_choice_id,
  sizeTemplateId: row.size_template_id,
  productSizeId: row.product_size_id,
  sizeCode: row.size_code,
  priceDelta: Number(row.price_delta),
  priceOverride:
    row.price_override != null ? Number(row.price_override) : null,
  isAvailable: Boolean(row.is_available),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapBundleComponent = (
  row: DatabaseBundleComponentRow,
): BundleComponentRecord => ({
  id: row.id,
  optionChoiceId: row.option_choice_id,
  componentType: row.component_type,
  productId: row.product_id,
  menuCategoryId: row.menu_category_id,
  itemGroupId: row.item_group_id,
  slotCode: row.slot_code,
  quantity: row.quantity,
  isRequired: Boolean(row.is_required),
  metadata: row.metadata ?? null,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapOptionChoice = (
  row: DatabaseOptionChoiceRow,
): OptionChoiceRecord => ({
  id: row.id,
  optionGroupId: row.option_group_id,
  name: row.name,
  slug: row.slug,
  description: row.description,
  isDefault: Boolean(row.is_default),
  maxQuantity: row.max_quantity,
  priceDelta: Number(row.price_delta),
  sortOrder: row.sort_order,
  isActive: Boolean(row.is_active),
  metadata: row.metadata ?? null,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  prices: (row.option_choice_prices ?? []).map(mapChoicePrice),
  bundleComponents: (row.bundle_components ?? []).map(mapBundleComponent),
});

const mapTarget = (row: DatabaseOptionGroupTargetRow): OptionGroupTargetRecord => ({
  id: row.id,
  optionGroupId: row.option_group_id,
  itemGroupId: row.item_group_id,
  menuCategoryId: row.menu_category_id,
  productId: row.product_id,
  sizeTemplateId: row.size_template_id,
  productSizeId: row.product_size_id,
  appliesToOrderTypes: row.applies_to_order_types ?? [
    "delivery",
    "takeaway",
    "dine_in",
  ],
  priority: row.priority,
  isRequiredOverride: row.is_required_override,
  createdAt: row.created_at,
  optionGroup: row.option_groups ? mapOptionGroup(row.option_groups) : null,
});

export async function listOptionGroups(): Promise<OptionGroupRecord[]> {
  const { data, error } = await adminClient
    .from("option_groups")
    .select("*")
    .order("priority", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) =>
    mapOptionGroup(row as DatabaseOptionGroupRow),
  );
}

export async function getOptionGroup(
  optionGroupId: string,
): Promise<OptionGroupDetail> {
  const { data, error } = await adminClient
    .from("option_groups")
    .select(
      `
        *,
        option_choices (
          *,
          option_choice_prices (*)
        ),
        option_group_targets (*)
      `,
    )
    .eq("id", optionGroupId)
    .single();

  if (error) {
    throw error;
  }

  const groupRow = data as DatabaseOptionGroupRow & {
    option_choices?: DatabaseOptionChoiceRow[];
    option_group_targets?: DatabaseOptionGroupTargetRow[];
  };

  const sortedChoices = (groupRow.option_choices ?? [])
    .map(mapOptionChoice)
    .sort((a, b) =>
      a.name.localeCompare(b.name, "tr", { sensitivity: "base" }),
    );

  return {
    ...mapOptionGroup(groupRow),
    choices: sortedChoices,
    targets: (groupRow.option_group_targets ?? []).map(mapTarget),
  };
}

export interface UpsertOptionGroupInput {
  id?: string;
  name: string;
  slug: string;
  description?: string | null;
  selectionType: OptionSelectionType;
  isRequired?: boolean;
  minSelections?: number;
  maxSelections?: number | null;
  appliesToOrderTypes?: string[];
  priority?: number;
  metadata?: Record<string, unknown> | null;
}

export async function upsertOptionGroup(
  input: UpsertOptionGroupInput,
): Promise<OptionGroupRecord> {
  const payload = {
    name: input.name,
    slug: input.slug,
    description: input.description ?? null,
    selection_type: input.selectionType,
    is_required: input.isRequired ?? false,
    min_selections: input.minSelections ?? 0,
    max_selections: input.maxSelections ?? null,
    applies_to_order_types:
      input.appliesToOrderTypes ?? ["delivery", "takeaway", "dine_in"],
    priority: input.priority ?? 100,
    metadata: input.metadata ?? null,
  };

  const { data, error } = await adminClient
    .from("option_groups")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return mapOptionGroup(data as DatabaseOptionGroupRow);
}

export async function deleteOptionGroup(optionGroupId: string) {
  const { error } = await adminClient
    .from("option_groups")
    .delete()
    .eq("id", optionGroupId);
  if (error) {
    throw error;
  }
}

export interface UpsertOptionChoiceInput {
  id?: string;
  optionGroupId: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  isDefault?: boolean;
  maxQuantity?: number | null;
  priceDelta?: number;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown> | null;
}

export async function upsertOptionChoice(
  input: UpsertOptionChoiceInput,
): Promise<OptionChoiceRecord> {
  const payload = {
    option_group_id: input.optionGroupId,
    name: input.name,
    slug: input.slug ?? null,
    description: input.description ?? null,
    is_default: input.isDefault ?? false,
    max_quantity: input.maxQuantity ?? null,
    price_delta: input.priceDelta ?? 0,
    sort_order: input.sortOrder ?? 0,
    is_active: input.isActive ?? true,
    metadata: input.metadata ?? null,
  };

  const { data, error } = await adminClient
    .from("option_choices")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select(
      `
        *,
        option_choice_prices (*)
      `,
    )
    .single();

  if (error) {
    throw error;
  }

  return mapOptionChoice(data as DatabaseOptionChoiceRow);
}

export async function deleteOptionChoice(optionChoiceId: string) {
  const { error } = await adminClient
    .from("option_choices")
    .delete()
    .eq("id", optionChoiceId);
  if (error) {
    throw error;
  }
}

export interface UpsertOptionChoicePriceInput {
  id?: string;
  optionChoiceId: string;
  sizeTemplateId?: string | null;
  productSizeId?: string | null;
  sizeCode?: string | null;
  priceDelta: number;
  priceOverride?: number | null;
  isAvailable?: boolean;
}

export async function upsertOptionChoicePrice(
  input: UpsertOptionChoicePriceInput,
): Promise<OptionChoicePriceRecord> {
  if (
    !input.sizeTemplateId &&
    !input.productSizeId &&
    !input.sizeCode
  ) {
    throw new Error(
      "Fiyat satırı oluşturmak için boyut template'i, ürün boyutu veya boyut kodu seçmelisiniz.",
    );
  }

  const payload = {
    option_choice_id: input.optionChoiceId,
    size_template_id: input.sizeTemplateId ?? null,
    product_size_id: input.productSizeId ?? null,
    size_code: input.sizeCode ?? null,
    price_delta: input.priceDelta,
    price_override: input.priceOverride ?? null,
    is_available: input.isAvailable ?? true,
  };

  const { data, error } = await adminClient
    .from("option_choice_prices")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return mapChoicePrice(data as DatabaseOptionChoicePriceRow);
}

export async function deleteOptionChoicePrice(optionChoicePriceId: string) {
  const { error } = await adminClient
    .from("option_choice_prices")
    .delete()
    .eq("id", optionChoicePriceId);
  if (error) {
    throw error;
  }
}

export interface UpsertOptionGroupTargetInput {
  id?: string;
  optionGroupId: string;
  itemGroupId?: string | null;
  menuCategoryId?: string | null;
  productId?: string | null;
  sizeTemplateId?: string | null;
  productSizeId?: string | null;
  appliesToOrderTypes?: string[];
  priority?: number;
  isRequiredOverride?: boolean | null;
}

export async function upsertOptionGroupTarget(
  input: UpsertOptionGroupTargetInput,
): Promise<OptionGroupTargetRecord> {
  if (
    !input.itemGroupId &&
    !input.menuCategoryId &&
    !input.productId &&
    !input.sizeTemplateId &&
    !input.productSizeId
  ) {
    throw new Error(
      "Seçenek grubunu ilişkilendirmek için en az bir hedef seçmelisiniz.",
    );
  }

  const payload = {
    option_group_id: input.optionGroupId,
    item_group_id: input.itemGroupId ?? null,
    menu_category_id: input.menuCategoryId ?? null,
    product_id: input.productId ?? null,
    size_template_id: input.sizeTemplateId ?? null,
    product_size_id: input.productSizeId ?? null,
    applies_to_order_types:
      input.appliesToOrderTypes ?? ["delivery", "takeaway", "dine_in"],
    priority: input.priority ?? 100,
    is_required_override: input.isRequiredOverride ?? null,
  };

  const { data, error } = await adminClient
    .from("option_group_targets")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return mapTarget(data as DatabaseOptionGroupTargetRow);
}

export async function deleteOptionGroupTarget(targetId: string) {
  const { error } = await adminClient
    .from("option_group_targets")
    .delete()
    .eq("id", targetId);
  if (error) {
    throw error;
  }
}

export async function listOptionGroupTargetsForProduct(
  productId: string,
  productSizeIds?: string[],
): Promise<OptionGroupTargetRecord[]> {
  const selectColumns = `
    *,
    option_groups (*)
  `;

  const results: DatabaseOptionGroupTargetRow[] = [];

  const { data: productRows, error: productError } = await adminClient
    .from("option_group_targets")
    .select(selectColumns)
    .eq("product_id", productId);

  if (productError) {
    throw productError;
  }

  if (Array.isArray(productRows)) {
    results.push(
      ...((productRows as unknown as DatabaseOptionGroupTargetRow[]) ?? []),
    );
  }

  if (productSizeIds && productSizeIds.length > 0) {
    const { data: sizeRows, error: sizeError } = await adminClient
      .from("option_group_targets")
      .select(selectColumns)
      .in("product_size_id", productSizeIds);

    if (sizeError) {
      throw sizeError;
    }

    if (Array.isArray(sizeRows)) {
      results.push(
        ...((sizeRows as unknown as DatabaseOptionGroupTargetRow[]) ?? []),
      );
    }
  }

  const deduped = Array.from(
    results.reduce<Map<string, DatabaseOptionGroupTargetRow>>(
      (acc, row) => acc.set(row.id, row),
      new Map(),
    ).values(),
  ).sort((a, b) => a.priority - b.priority);

  return deduped.map((row) =>
    mapTarget(row as unknown as DatabaseOptionGroupTargetRow),
  );
}

export interface UpsertBundleComponentInput {
  id?: string;
  optionChoiceId: string;
  componentType: BundleComponentType;
  productId?: string | null;
  menuCategoryId?: string | null;
  itemGroupId?: string | null;
  slotCode?: string | null;
  quantity?: number;
  isRequired?: boolean;
  metadata?: Record<string, unknown> | null;
}

export async function upsertBundleComponent(
  input: UpsertBundleComponentInput,
): Promise<BundleComponentRecord> {
  if (input.componentType === "product" && !input.productId) {
    throw new Error("Ürün bileşeni için bir ürün seçmelisiniz.");
  }
  if (input.componentType === "category" && !input.menuCategoryId) {
    throw new Error("Kategori bileşeni için bir kategori seçmelisiniz.");
  }
  if (
    input.componentType === "group_slot" &&
    !input.slotCode &&
    !input.itemGroupId
  ) {
    throw new Error(
      "Slot bileşeni için bir kod veya öğe grubu tanımlamalısınız.",
    );
  }

  const payload = {
    option_choice_id: input.optionChoiceId,
    component_type: input.componentType,
    product_id: input.productId ?? null,
    menu_category_id: input.menuCategoryId ?? null,
    item_group_id: input.itemGroupId ?? null,
    slot_code: input.slotCode ?? null,
    quantity: input.quantity ?? 1,
    is_required: input.isRequired ?? false,
    metadata: input.metadata ?? null,
  };

  const { data, error } = await adminClient
    .from("bundle_components")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return mapBundleComponent(data as DatabaseBundleComponentRow);
}

export async function deleteBundleComponent(bundleComponentId: string) {
  const { error } = await adminClient
    .from("bundle_components")
    .delete()
    .eq("id", bundleComponentId);
  if (error) {
    throw error;
  }
}
